import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FRAME_END } from '@/lib/heroFrames';
const FRAME_COUNT = 486;
// Toplu yüklemede aynı anda açık istek sayısı (masaüstü).
const DESKTOP_CONCURRENCY = 4;
const MOBILE_CONCURRENCY = 2;
// Seyrek geçişte her kaçıncı kare önce alınır.
const KEYFRAME_EVERY = 12;
// Mobilde her kare indirilmez: 486 × ~74 KB = ~36 MB kabul edilemez.
const MOBILE_STRIDE = 6;
const SAVE_DATA_STRIDE = 12;
const clamp = (value: number, min = 0, max = 1) =>
  Math.min(max, Math.max(min, value));

const CAPTION_LEAD = 4;

const frameUrl = (index: number) =>
  `${process.env.PUBLIC_URL}/frames/home/frame_${String(index).padStart(3, '0')}.webp`;

const LaserFrameHero = () => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const loadedRef = useRef<boolean[]>(Array(FRAME_COUNT).fill(false));
  const drawnRef = useRef(-1);
  const targetRef = useRef(0);
  const progressRef = useRef(0);
  const readyRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  const drawFrame = (requested: number, force = false) => {
    const canvas = canvasRef.current;
    if (!canvas || (!force && requested === drawnRef.current)) return;

    let index = clamp(requested, 0, FRAME_COUNT - 1);
    if (!loadedRef.current[index]) {
      let previous = index;
      while (previous >= 0 && !loadedRef.current[previous]) previous -= 1;
      if (previous < 0) return;
      index = previous;
    }

    const image = imagesRef.current[index];
    if (!image?.naturalWidth) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (canvas.width !== Math.floor(width * dpr))
      canvas.width = Math.floor(width * dpr);
    if (canvas.height !== Math.floor(height * dpr))
      canvas.height = Math.floor(height * dpr);

    const context = canvas.getContext('2d');
    if (!context) return;
    const scale = Math.max(
      canvas.width / image.naturalWidth,
      canvas.height / image.naturalHeight,
    );
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    context.fillStyle = '#000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      image,
      (canvas.width - drawWidth) / 2,
      (canvas.height - drawHeight) / 2,
      drawWidth,
      drawHeight,
    );
    drawnRef.current = requested;
  };

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  // ── KARE YÜKLEME ────────────────────────────────────────────────────────────
  // Kare dizisi ~36 MB (486 × ~74 KB). Eskiden mount anında 8 paralel işçiyle
  // TAMAMI indiriliyordu; bu, belge/stil/yazı tipi gibi kritik kaynaklarla
  // yarışıp ilk boyamayı geciktiriyordu (PageSpeed: FCP 3.1s, Speed Index 6.7s
  // ve raporun tamamını dolduran yüzlerce ~64 KB'lık istek).
  //
  // Yeni düzen:
  //   1) İlk kare HEMEN → tuval boş kalmaz.
  //   2) Gerisi window.load SONRASINA ertelenir → kritik yolu tıkamaz.
  //   3) Önce SEYREK bir geçiş (stride) → tüm parkur erkenden sürülebilir olur.
  //   4) Kalanlar, o anki scroll hedefine YAKINLIK sırasına göre doldurulur.
  // drawFrame yüklenmemiş kareyi en yakın yüklenmiş olanla değiştirdiği için
  // eksik kareler kademeli olarak keskinleşir; kopukluk oluşmaz.
  //
  // MOBİL: dar ekranda her kare indirilmez (MOBILE_STRIDE) — 36 MB mobil veri
  // kabul edilebilir değil. Oynatma bir tık daha az akıcı ama görünüm aynı.
  useEffect(() => {
    let cancelled = false;
    imagesRef.current = new Array(FRAME_COUNT);
    loadedRef.current = Array(FRAME_COUNT).fill(false);
    readyRef.current = false;

    const markFrameReady = () => {
      if (cancelled || readyRef.current) return;
      readyRef.current = true;
      requestAnimationFrame(() => {
        if (cancelled) return;
        trackRef.current?.setAttribute('data-home-frame-ready', 'true');
        window.dispatchEvent(new Event('ktp-home-frame-ready'));
      });
    };

    const load = (index: number) =>
      new Promise<void>((resolve) => {
        const image = new Image();
        image.decoding = 'async';
        (
          image as HTMLImageElement & { fetchPriority?: 'high' | 'low' }
        ).fetchPriority = index === 0 ? 'high' : 'low';
        image.onload = () => {
          if (!cancelled) {
            loadedRef.current[index] = true;
            if (index === 0 || index === targetRef.current)
              drawFrame(targetRef.current, true);
            if (index === 0) markFrameReady();
          }
          resolve();
        };
        image.onerror = () => {
          if (index === 0) markFrameReady();
          resolve();
        };
        image.src = frameUrl(index + 1);
        imagesRef.current[index] = image;
      });

    // 1) İlk kare — beklemeden.
    void load(0);

    const narrow =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches;
    const saveData =
      (navigator as Navigator & { connection?: { saveData?: boolean } })
        .connection?.saveData ?? false;
    const stride = saveData ? SAVE_DATA_STRIDE : narrow ? MOBILE_STRIDE : 1;
    const concurrency =
      narrow || saveData ? MOBILE_CONCURRENCY : DESKTOP_CONCURRENCY;

    // İndirilecek kare kümesi (mobilde seyrekleştirilmiş).
    const wanted: number[] = [];
    for (let i = stride; i < FRAME_COUNT; i += stride) wanted.push(i);

    const queued = new Set<number>();
    // O anki hedefe EN YAKIN, henüz alınmamış kare.
    const nextIndex = () => {
      const t = targetRef.current;
      let best = -1;
      let bestDist = Infinity;
      for (const i of wanted) {
        if (loadedRef.current[i] || queued.has(i)) continue;
        const d = Math.abs(i - t);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    };

    const runBulk = async () => {
      // 3) Seyrek geçiş: parkurun tamamı kabaca sürülebilir olsun.
      const coarse = [];
      for (let i = stride; i < FRAME_COUNT; i += stride * KEYFRAME_EVERY)
        coarse.push(i);
      for (const i of coarse) {
        if (cancelled) return;
        queued.add(i);
        await load(i);
      }
      // 4) Kalanlar — hedefe yakınlık sırasına göre, sınırlı eşzamanlılıkla.
      const worker = async () => {
        while (!cancelled) {
          const i = nextIndex();
          if (i < 0) return;
          queued.add(i);
          await load(i);
        }
      };
      await Promise.all(Array.from({ length: concurrency }, worker));
    };

    // 2) Toplu yükleme window.load SONRASINDA (ve tarayıcı boştayken) başlar.
    let idle = 0;
    const schedule = () => {
      const ric = (
        window as Window &
          typeof globalThis & {
            requestIdleCallback?: (cb: () => void) => number;
          }
      ).requestIdleCallback;
      idle = ric ? ric(() => void runBulk()) : window.setTimeout(runBulk, 200);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    const resize = () => drawFrame(targetRef.current, true);
    window.addEventListener('resize', resize);
    const track = trackRef.current;
    return () => {
      cancelled = true;
      track?.removeAttribute('data-home-frame-ready');
      window.removeEventListener('resize', resize);
      window.removeEventListener('load', schedule);
      window.clearTimeout(idle);
    };
    // drawFrame intentionally reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let animationFrame = 0;
    const update = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const track = trackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        const distance = rect.height - window.innerHeight;
        const nextProgress = reducedMotion
          ? 1
          : clamp(-rect.top / Math.max(1, distance));
        const frameProgress = clamp(nextProgress / FRAME_END);
        targetRef.current = Math.round(frameProgress * (FRAME_COUNT - 1));
        drawFrame(targetRef.current);
        if (
          Math.abs(nextProgress - progressRef.current) > 0.003 ||
          nextProgress === 0 ||
          nextProgress === 1
        ) {
          progressRef.current = nextProgress;
          setProgress(nextProgress);
        }
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reducedMotion]);

  // Frame başlığı — Etkinlik/Basın bölümleriyle (ScrollDeck) AYNI daktilo efekti:
  // kareler oynarken harf harf YAZILIR (yazma kafasının önündeki LEAD harfler
  // erken, kırmızı ve azalan opaklıkla belirir), kısa süre durur, lazer finali
  // başlamadan harf harf geri SİLİNİR.
  const captionText = t('home.frameCaption');
  const captionWrite = clamp((progress - 0.04) / 0.13); // yazılma 0.04→0.17
  const captionErase = clamp((progress - 0.24) / 0.06); // silinme 0.24→0.30 (baştan)
  const captionWriteHead = captionText.length * captionWrite; // yazma kafası
  const captionEraseHead = captionText.length * captionErase; // silme kafası (baştan)
  const captionHeadChar = Math.floor(captionWriteHead);
  const captionHeadActive = captionWrite > 0 && captionWrite < 1;
  const captionEraseActive = captionErase > 0 && captionErase < 1;
  // Her harfin opaklığı + turuncu (yazma/silme kafası) durumu. Silme de baştan
  // ilerleyen turuncu bir kafayla yapılır (yazma efektinin aynası).
  const captionCharState = (gi: number): { op: number; orange: boolean } => {
    // Silme fazı: baştan ilerleyen kafa; geçilenler yok, kafanın önündeki
    // harfler turuncu ve azalan opaklıkla erir.
    if (captionErase > 0) {
      const ed = gi - captionEraseHead; // <0 silindi, 0 = kafa, >0 henüz duruyor
      if (ed <= 0) return { op: 0, orange: false };
      if (ed <= CAPTION_LEAD)
        return {
          op: clamp(ed / (CAPTION_LEAD + 1)) ** 1.3,
          orange: captionEraseActive,
        };
    }
    // Yazma fazı: kafanın önündeki (henüz yazılmamış) harfler turuncu belirir.
    const d = gi - captionHeadChar; // 0 = kafa, <0 yazıldı, >0 ileride
    if (d <= 0) return { op: 1, orange: false };
    if (d > CAPTION_LEAD) return { op: 0, orange: false };
    return {
      op: clamp(1 - d / (CAPTION_LEAD + 1)) ** 1.3,
      orange: captionHeadActive,
    };
  };
  const captionGlow = clamp(captionWrite * 5) * (1 - captionErase); // alt degrade
  // Çok satırlı (\n) metin: her satır blok; yazılan kısım hep en altta kalır,
  // henüz başlamamış satır render edilmez.
  let captionOffset = 0;
  const captionLines =
    captionWrite <= 0
      ? null
      : captionText.split('\n').map((line, idx) => {
          if (idx > 0) captionOffset += 1; // \n bir karakter sayılır
          const start = captionOffset;
          captionOffset += line.length;
          if (captionHeadChar + CAPTION_LEAD < start) return null;
          return (
            <span key={idx} className="block">
              {Array.from(line).map((ch, j) => {
                const { op, orange } = captionCharState(start + j);
                return (
                  <span
                    key={j}
                    style={{ opacity: op }}
                    className={orange ? 'text-[#E24733]' : undefined}
                  >
                    {ch}
                  </span>
                );
              })}
            </span>
          );
        });

  // Tanıtım metni — kare dizisinin PATLAMA/final bölümünde (0.50→0.64) aynı
  // daktilo efektiyle ortada yazılır ve SİLİNMEZ; sonraki bölüm (sheet) alttan
  // kayarak üzerini örtene kadar patlama karelerinin üstünde kalır.
  const introWrite = clamp((progress - 0.5) / 0.14); // yazma kafası 0→1
  const introText = t('home.frameParagraph');
  const introWriteHead = introText.length * introWrite;
  const introWriteChar = Math.floor(introWriteHead);
  const introWriteActive = introWrite > 0 && introWrite < 1;
  // Frame başlığıyla aynı: yazma kafasının önündeki harfler turuncu belirir.
  const introCharState = (gi: number): { op: number; orange: boolean } => {
    const d = gi - introWriteChar;
    if (d <= 0) return { op: 1, orange: false };
    if (d > CAPTION_LEAD) return { op: 0, orange: false };
    return {
      op: clamp(1 - d / (CAPTION_LEAD + 1)) ** 1.3,
      orange: introWriteActive,
    };
  };
  const introChars =
    introWrite <= 0
      ? null
      : Array.from(introText).map((ch, j) => {
          const { op, orange } = introCharState(j);
          return (
            <span
              key={j}
              style={{ opacity: op }}
              className={orange ? 'text-[#E24733]' : undefined}
            >
              {ch}
            </span>
          );
        });

  // Parkur yüksekliği oynatma hızını belirler: kareler scroll'un ilk %66'sına
  // yayıldığından yükseklik arttıkça oynatma yavaşlar (486 kare için 460vh;
  // eski 409 karelik setin 340vh temposundan bir tık daha yavaş).
  return (
    // bg-white DEĞİL: mobilde 100lvh sticky yüzey parkurun dibiyle birebir
    // örtüşmediğinde altta beyaz bir çizgi kalıyordu.
    <section ref={trackRef} className="relative h-[calc(460vh+5rem)] bg-black">
      <div className="sticky top-0 h-[100lvh] w-full overflow-hidden bg-black">
        {/* Yazısız ilk ekran posteri: canvas ilk kareyi çizene kadar aynı sahne
            görünür kalır ve Lighthouse için görsel LCP adayı sağlar. */}
        <img
          src={`${process.env.PUBLIC_URL}/frames/home/frame_001.webp`}
          alt=""
          aria-hidden
          width={1920}
          height={1080}
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 z-0 h-full w-full object-cover"
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 z-10 h-full w-full"
        />

        {/* Frame başlığı — Etkinlik/Basın ile aynı daktilo efektiyle yazılır
            ve silinir. Okunabilirlik için alttan koyu degrade. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex flex-col items-center justify-end px-5 pb-[8%] sm:px-6">
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/25 to-transparent"
            style={{ opacity: captionGlow }}
          />
          <h2
            className="relative max-w-5xl text-center font-bricolage text-3xl font-semibold tracking-tight text-white sm:text-5xl md:text-7xl"
            style={{
              textShadow:
                '0 2px 14px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)',
            }}
          >
            {captionLines}
          </h2>
        </div>

        {/* Tanıtım metni — logo aşağı inerken ortada, dikeyde ortanın bir tık
            üstünde, frame başlığıyla aynı daktilo efektiyle yazılır. */}
        {introChars && (
          <div className="pointer-events-none absolute inset-x-0 top-[44%] z-20 -translate-y-1/2 px-6 sm:px-10">
            <p
              className="mx-auto max-w-3xl text-center font-bricolage text-lg font-medium leading-relaxed tracking-tight text-white sm:text-2xl md:text-3xl"
              style={{
                textShadow:
                  '0 2px 16px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.95)',
              }}
            >
              {introChars}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default LaserFrameHero;
